import React, { createContext, useContext, useLayoutEffect, useState } from 'react';
import { Theme, getThemePreference, setTheme as saveTheme, applyTheme } from './theme';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * ThemeProvider component - wrap your app with this to enable dark mode
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => getThemePreference());
  const [mounted, setMounted] = useState(false);

  // Apply theme before paint to avoid FOUC
  useLayoutEffect(() => {
    applyTheme(theme);
    setMounted(true);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    saveTheme(newTheme);
    setThemeState(newTheme);
  };

  const handleSetTheme = (newTheme: Theme) => {
    saveTheme(newTheme);
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme: handleSetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme context
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
